import { differenceInDays } from "date-fns";

import { Filter, IDateRangeWithCompareFilter } from "constants/filters";
import { ReportDistribution } from "constants/reports/distribution";
import { Last7DaysMode } from "constants/reports/last-7-days-mode";
import { ReportType } from "constants/reports/report-type";
import { deserializeCustomDateRangeURLParams, deserializeCustomDateURLParams } from "helpers/filters";
import { DetailedSource } from "interfaces/entities/ticket";

import { ReportsViewAction, ReportsViewActionsNames } from "./actions";
import { getReportId } from "./helpers";
import {
  IReportsViewAgentFilter,
  IReportsViewCacheExpirationTime,
  IReportsViewData,
  IReportsViewFilters,
  IReportsViewGroupFilter,
} from "./interfaces";

export interface IReportsViewState {
  inited: boolean;
  currentView: ReportType;
  filters: IReportsViewFilters;
  filtersOrder: Filter[];
  distribution: ReportDistribution;
  last7DaysMode: Last7DaysMode;
  data: IReportsViewData;
  cacheExpirationTime: IReportsViewCacheExpirationTime;
  lastAvailableSources: DetailedSource[];
}

export const initialState: IReportsViewState = {
  inited: false,
  currentView: ReportType.Last7days,
  filters: {
    action: undefined,
    agent: undefined,
    // @ts-expect-error TS(2353)
    date: deserializeCustomDateURLParams(new Date().toString()),
    // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
    dateRange: deserializeCustomDateRangeURLParams("-6D", undefined),
    pageSize: 41,
    group: undefined,
    priority: undefined,
    detailedSource: undefined,
    tag: undefined,
    target: undefined,
    language: undefined,
  },
  filtersOrder: [Filter.DateRange],
  distribution: ReportDistribution.Day,
  // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'Last7DaysMo... Remove this comment to see the full error message
  last7DaysMode: null,
  data: {},
  cacheExpirationTime: {},
  lastAvailableSources: [],
};

const reportsReducer = (state: IReportsViewState = initialState, action: ReportsViewAction): IReportsViewState => {
  switch (action.type) {
    case ReportsViewActionsNames.INITED: {
      return {
        ...state,
        inited: true,
      };
    }

    case ReportsViewActionsNames.SET_CURRENT_VIEW: {
      const currentView = action.payload.reportType;
      return {
        ...state,
        currentView,
      };
    }

    case ReportsViewActionsNames.SET_CACHE_EXPIRATION_TIME: {
      const { reportType, expirationTime } = action.payload;
      return {
        ...state,
        cacheExpirationTime: {
          ...state.cacheExpirationTime,
          [reportType]: expirationTime,
        },
      };
    }

    case ReportsViewActionsNames.SET_DATA: {
      const { reportType, data } = action.payload;
      return {
        ...state,
        data: {
          ...state.data,
          [reportType]: data,
        },
      };
    }

    case ReportsViewActionsNames.CREATE_SCHEDULED_REPORT_SUCCESS: {
      const { data } = action.payload;
      if (!data) {
        return { ...state };
      }
      // @ts-expect-error TS(2339): Property 'sortedIds' does not exist on type 'ISche... Remove this comment to see the full error message
      const { sortedIds } = state.data[ReportType.ScheduledReports];
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      const highlightedIds = state.data[ReportType.ScheduledReports].highlightedIds || [];
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      const reports = state.data[ReportType.ScheduledReports].data;

      sortedIds.push(data.id);
      highlightedIds.push(data.id);
      reports[data.id] = data;

      return {
        ...state,
        data: {
          ...state.data,
          [ReportType.ScheduledReports]: {
            sortedIds,
            highlightedIds,
            data: reports,
          },
        },
      };
    }

    case ReportsViewActionsNames.EDIT_SCHEDULED_REPORT_SUCCESS: {
      const { data } = action.payload;
      // @ts-expect-error TS(2339): Property 'sortedIds' does not exist on type 'ISche... Remove this comment to see the full error message
      let { sortedIds } = state.data[ReportType.ScheduledReports];
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      const reports = state.data[ReportType.ScheduledReports].data;
      const currentReportName = getReportId(data.typeCurrent, data.periodCurrent);

      delete reports[currentReportName];

      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sortedIds = sortedIds.filter((itemId: any) => itemId !== currentReportName);
      sortedIds.push(data.id);

      reports[data.id] = {
        ...reports[data.id],
        ...data,
      };

      return {
        ...state,
        data: {
          ...state.data,
          [ReportType.ScheduledReports]: {
            sortedIds,
            data: reports,
          },
        },
      };
    }

    case ReportsViewActionsNames.DELETE_SCHEDULED_REPORT_SUCCESS: {
      const {
        data: { id },
      } = action.payload;

      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      const reports = state.data[ReportType.ScheduledReports].data;
      delete reports[id];

      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      const sortedIds = state.data[ReportType.ScheduledReports].sortedIds.filter(itemId => itemId !== id);

      return {
        ...state,
        data: {
          ...state.data,
          [ReportType.ScheduledReports]: {
            sortedIds,
            data: reports,
          },
        },
      };
    }

    case ReportsViewActionsNames.SET_DISTRIBUTION: {
      const { type } = action.payload;

      let distribution = type;

      if (distribution === null) {
        const dateRange = state.filters[Filter.DateRange];

        // @ts-expect-error TS(18048)
        let diff = Math.abs(differenceInDays(dateRange.from, dateRange.to));

        // @ts-expect-error TS(18048)
        if (dateRange.compare) {
          diff = Math.max(
            // @ts-expect-error TS(18048)
            Math.abs(differenceInDays(dateRange.from, dateRange.to)),
            // @ts-expect-error TS(18048)
            Math.abs(differenceInDays(dateRange.compare.from, dateRange.compare.to))
          );
        }

        distribution = diff > 183 ? ReportDistribution.Month : ReportDistribution.Day;
      }

      return {
        ...state,
        distribution,
      };
    }

    case ReportsViewActionsNames.UPDATE_FILTER: {
      const filter = action.payload;

      let newFilters: IReportsViewFilters = { ...state.filters };
      let newFiltersOrder = [...state.filtersOrder];

      // @ts-expect-error TS(2339)
      if (!filter.name) {
        // @ts-expect-error TS(2322)
        newFilters = { ...state.filters, ...filter };
      }

      // @ts-expect-error TS(2339)
      if (filter.value === null) {
        // @ts-expect-error TS(2339)
        if (![Filter.DateRange].includes(filter.name)) {
          // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          newFilters[filter.name] = null;
          // @ts-expect-error TS(2339)
          newFiltersOrder = newFiltersOrder.filter(filterName => filterName !== filter.name);
        } else {
          // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          newFilters = { ...newFilters, [filter.name]: initialState.filters[filter.name] };
        }
      } else {
        // @ts-expect-error TS(2339)
        if (filter.name === Filter.Group) {
          const groupValue: IReportsViewGroupFilter = {
            // @ts-expect-error TS(2339)
            groups: filter.value as string[],
            // @ts-expect-error TS(2339)
            compare: filter.compare as string[],
          };

          // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          newFilters[filter.name] = groupValue;
          // @ts-expect-error TS(2339)
        } else if (filter.name === Filter.Agent) {
          const agentValue: IReportsViewAgentFilter = {
            // @ts-expect-error TS(2339)
            agents: filter.value as string[],
            // @ts-expect-error TS(2339)
            compare: filter.compare as string[],
          };

          // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          newFilters[filter.name] = agentValue;
          // @ts-expect-error TS(2339)
        } else if (filter.name === Filter.DateRange) {
          const dateRangeValue: IDateRangeWithCompareFilter = {
            // @ts-expect-error TS(2339)
            ...(filter.value as IDateRangeWithCompareFilter),
            // @ts-expect-error TS(2339)
            compare: filter.compare as IDateRangeWithCompareFilter,
          };

          // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          newFilters[filter.name] = dateRangeValue;
        } else {
          // FIXME: Fix this the next time the file is edited.
          // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          newFilters[filter.name] = filter.value as any;
        }

        // @ts-expect-error TS(2339)
        if (!newFiltersOrder.some(filterName => filterName === filter.name)) {
          // @ts-expect-error TS(2339)
          newFiltersOrder = [filter.name, ...newFiltersOrder];
        }
      }

      let { distribution } = state;

      // @ts-expect-error TS(2339)
      if (filter.name === Filter.DateRange) {
        // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        const dateRange = newFilters[filter.name];

        let diff = Math.abs(differenceInDays(dateRange.from, dateRange.to));

        if (dateRange.compare) {
          diff = Math.max(
            Math.abs(differenceInDays(dateRange.from, dateRange.to)),
            Math.abs(differenceInDays(dateRange.compare.from, dateRange.compare.to))
          );
        }

        const targetDistribution = diff > 183 ? ReportDistribution.Month : ReportDistribution.Day;

        if (distribution !== ReportDistribution.Hour && distribution !== targetDistribution) {
          distribution = targetDistribution;
        }
      }

      return {
        ...state,
        filters: newFilters,
        filtersOrder: newFiltersOrder,
        distribution,
      };
    }

    case ReportsViewActionsNames.SET_QUERY_PARAMS_FROM_URL: {
      const {
        action: actionName,
        author,
        agent,
        // @ts-expect-error TS(2339)
        date,
        dateRange,
        distribution = ReportDistribution.Day,
        group,
        priority,
        rating,
        detailedSource,
        tag,
        language,
        target,
        pageSize,
      } = action.payload.params;
      const nextId = action.payload.params[Filter.NextId];
      const prevId = action.payload.params[Filter.PrevId];
      const nextValue = action.payload.params[Filter.NextValue];
      const prevValue = action.payload.params[Filter.PrevValue];

      const newFilterValues: IReportsViewFilters = {
        ...(author && { author }),
        ...(action && { action: actionName }),
        ...(agent && { agent }),
        ...(dateRange && { dateRange }),
        ...(date && { date }),
        ...(group && { group }),
        ...(rating && { rating }),
        ...(tag && { tag }),
        ...(target && { target }),
        ...(priority && { priority }),
        ...(detailedSource && { detailedSource }),
        ...(pageSize && { pageSize }),
        ...(nextId && { [Filter.NextId]: nextId }),
        ...(prevId && { [Filter.PrevId]: prevId }),
        ...(nextValue && { [Filter.NextValue]: nextValue }),
        ...(prevValue && { [Filter.PrevValue]: prevValue }),
        ...(language && { [Filter.Language]: language }),
      };

      return {
        ...state,
        filters: {
          ...initialState.filters,
          ...newFilterValues,
        },
        distribution,
        filtersOrder: [
          Filter.DateRange,
          Filter.Agent,
          Filter.Group,
          Filter.Tag,
          Filter.Rating,
          Filter.Priority,
          Filter.DetailedSource,
          Filter.Author,
          Filter.Action,
          Filter.Target,
          Filter.PageSize,
          Filter.NextId,
          Filter.NextValue,
          Filter.PrevId,
          Filter.PrevValue,
          Filter.Language,
          // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        ].filter(f => Boolean(newFilterValues[f] || f === Filter.DateRange)),
      };
    }

    case ReportsViewActionsNames.SET_LAST_7_DAYS_MODE: {
      const { last7DaysMode } = action.payload;

      return {
        ...state,
        last7DaysMode,
      };
    }

    case ReportsViewActionsNames.SET_LAST_AVAILABLE_SOURCES: {
      const { sources } = action.payload;

      return {
        ...state,
        lastAvailableSources: sources,
      };
    }

    default:
      return state;
  }
};

export default reportsReducer;
